import { useState } from 'react'
import { useRouter } from 'next/router'

import { useAdFree, useAdFreeTrialStatus } from 'hooks/adFree'

import UserService from 'services/user.service'
import ToastService from 'services/toast.service'
import Analytics from 'utils/googleAnalytics'
import Link from 'components/elements/Link'
import PhatButton from 'components/formElements/PhatButton'

import styles from './freeTrailDetails.module.scss'
import { useTimeout } from 'hooks/componentDidMount'

type Props = {
  onCancel?: () => void
}

const FreeTrialDetails = ({ onCancel }: Props) => {
  const router = useRouter()

  const [adFreeStatus, freeTrialExpiresDate] = useAdFreeTrialStatus()

  const [loading, setLoading] = useState(false)

  const adFree = useAdFree()
  const enableCancel = useTimeout(3000)

  const handleStartTrial = () => {
    setLoading(true)

    UserService.claimAdFreeTrial()
      .then(() => {
        ToastService.create('Ad free accepted - reloading page...', 'User service', 'success')
        Analytics.signalEvent(`Free trial accepted`)

        setTimeout(() => router.reload(), 1500)
      })
      .catch(error => {
        ToastService.create(error, 'User service', 'error')
        setLoading(false)
      })
  }

  return (
    <>
      <h3>Ad-free trial</h3>

      {adFreeStatus === 'IN_PROGRESS' && (
        <p>
          You've claimed a 30 day ad-free experience on Archidekt. Your trial will end on{' '}
          {freeTrialExpiresDate.toLocaleDateString()} {freeTrialExpiresDate.toLocaleTimeString()}. At any time, you can
          extend your ad-free experience by supporting us for as little as $2/month on{' '}
          <Link href="https://www.patreon.com/archidekt/membership" className={styles.ctaLink}>
            Patreon
          </Link>
          .
        </p>
      )}

      {adFreeStatus === 'NOT_STARTED' && !adFree && (
        <>
          <p>Okay, we know you hate the floating button, but hear us out. </p>

          <p>
            We feel that Archidekt is best experienced when used without ads. We feel so strongly about this that we
            offer all accounts a one-time-use 30 days of ad-free, no strings attached. Clicking the button below will
            start your ad-free trial immediately.{' '}
            <b>
              We won't ask you to input a payment method like those skeevy sites that charge you if you forget to cancel
              before the free-trial is up.
            </b>
          </p>

          <p>
            At any time, you can extend your ad-free experience by supporting us for as little as $2/month on{' '}
            <Link href="https://www.patreon.com/archidekt/membership" className={styles.ctaLink}>
              Patreon
            </Link>
            . This goes a long way toward helping us to continue to develop Archidekt, so we greatly appreciate your
            contribution!
          </p>

          <p>
            Once you accept the free trial, the floating button at the bottom of the page will go away forever,
            regardless of whether you choose to continue supporting us on Patreon or not.
          </p>
        </>
      )}

      {adFreeStatus === 'EXPIRED' && !adFree && (
        <p>
          You've already used your 30 day ad-free trial. At any time, you can become ad-free again by supporting us for
          as little as $2/month on{' '}
          <Link href="https://www.patreon.com/archidekt/membership" className={styles.ctaLink}>
            Patreon
          </Link>
          .
        </p>
      )}

      {adFreeStatus === 'NOT_STARTED' && !adFree && (
        <div className={styles.controls}>
          {onCancel && (
            <PhatButton disabled={!enableCancel || loading} color="red" icon="close" onClick={onCancel}>
              No, thank You
            </PhatButton>
          )}

          <PhatButton icon="arrow circle right" color="blue" onClick={handleStartTrial} disabled={loading}>
            {loading ? 'Updating account...' : 'Start 30 day ad-free'}
          </PhatButton>
        </div>
      )}
    </>
  )
}

export default FreeTrialDetails
